import { useEffect, useRef } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import { useAuth } from "@repo/auth";
import { useTranslation } from "@repo/i18n";
import { css } from "@repo/styled-system/css";
import { isSessionEnded } from "../shell/session";

/**
 * Invisible SSO redirect handler — there is no login PAGE anymore. `RequireAuth` forwards unauthenticated
 * users straight to Keycloak (which, in staging/prod, forwards straight to Google — no interstitial, no
 * password form). This route survives only to honour the `?idp=<alias>` deep-link (e.g. `/login?idp=entra`)
 * that platform operators use to reach a specific, hidden identity provider; a plain hit just forwards to the
 * default provider. It renders nothing but a brief "Redirecting…" state before the browser leaves.
 */
export function Login() {
  const { isAuthenticated, isLoading, login } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const idpHint = searchParams.get("idp") ?? undefined;
  // Sent here by a 401: the server has rejected the session, so a client that still holds an expired
  // user (and so still reads as authenticated) signs in again instead of bouncing back to "/".
  const sessionEnded = isSessionEnded(location.state);
  // Decide only once the stored session has loaded: on a cold page load the state reads "signed
  // out" until it has, which sent signed-in users through the IdP again.
  const signedIn = !isLoading && isAuthenticated && !sessionEnded;
  const mustSignIn = !isLoading && (!isAuthenticated || sessionEnded);

  useEffect(() => {
    if (signedIn) navigate("/", { replace: true });
  }, [signedIn, navigate]);

  // Forward once. `kc_idp_hint` (from `?idp`) overrides Keycloak's default provider, so the deep-link
  // still reaches its IdP even though the realm's browser flow otherwise jumps straight to Google.
  const started = useRef(false);
  useEffect(() => {
    if (mustSignIn && !started.current) {
      started.current = true;
      login(idpHint ? { idpHint } : undefined);
    }
  }, [mustSignIn, idpHint, login]);

  return (
    <main
      className={css({
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minH: "100dvh",
        bg: "bg.canvas",
      })}
    >
      <p className={css({ color: "fg.muted", fontSize: "sm" })}>{t("login.redirecting")}</p>
    </main>
  );
}
